import type { ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import type { SupplyReportWithLinesDto } from '@eombor/contracts';
import { Badge, Button, type BadgeTone } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import {
  supplyPeriodText,
  supplySourceText,
  supplyStatusText,
} from '../../../shared/i18n/labels.js';

const tone = (status: string): BadgeTone => {
  if (status === 'SENT') return 'warn';
  if (status === 'PARTIAL') return 'info';
  return status === 'ACCEPTED' ? 'success' : 'neutral';
};

interface MetaItemProps {
  label: string;
  children: ReactNode;
}

/**
 * META MAYDONI — yorliq TEPADA, qiymat ostida.
 *
 * Ikkalasi ham bir SHRIFT OILASIDA. Ilgari qiymatlar monospace edi
 * (`.tabular` sinfi `font-family: var(--font-mono)` ham beradi),
 * yorliqlar esa sans — bitta ustunda ikki xil shrift turardi va
 * ko'z ularni bog'liq deb o'qimasdi. Endi farq faqat o'lchov va
 * rangda; sonlar tekisligi uchun `tabular-nums` yetadi va u shrift
 * oilasini o'zgartirmaydi.
 *
 * Monospace faqat KOD uchun qoldi: hujjat raqami, katalog kodi,
 * manzil. U yerda belgini noto'g'ri o'qish qimmatga tushadi.
 */
const MetaItem = ({ label, children }: MetaItemProps) => (
  <div className="flex min-w-0 flex-col gap-1">
    <dt className="text-xs font-semibold tracking-wide text-text-muted uppercase">{label}</dt>
    <dd className="truncate text-sm font-medium tabular-nums text-text">{children}</dd>
  </div>
);

export interface SupplyDocHeaderProps {
  doc: SupplyReportWithLinesDto;
  onBack: () => void;
}

/**
 * HUJJAT SARLAVHASI — sahifadagi eng tinch zona.
 *
 * Bu yerdagi ma'lumot bir marta o'qiladi va keyin fon bo'lib qoladi,
 * shuning uchun u ish maydonidan (jadval) past bosim bilan chiziladi:
 * bitta H1, bitta nishon va bir qatorli meta.
 *
 * Meta qatoridagi to'rt maydon bir xil tuzilmada — yorliq va qiymat
 * bir vertikalda, bir xil balandlikda. Ilgari ular `grid` da
 * «yorliq | qiymat» juftligi bo'lib yotardi va ustunlar eni mazmunga
 * qarab sakrar, hech narsa tekislanmasdi.
 *
 * «SATRLAR 233» ustuni ATAYLAB yo'q: yonidagi «JOYLASHTIRILGAN 43 / 233»
 * jamini allaqachon aytadi va ikkita 233 yonma-yon turganda ularning
 * qaysi biri nima ekani o'qilmasdi.
 */
export const SupplyDocHeader = ({ doc, onBack }: SupplyDocHeaderProps) => {
  const period = supplyPeriodText(doc.periodLabel);
  const source = supplySourceText(doc.sourceLabel);

  return (
    <header className="flex flex-col gap-4">
      <Button variant="ghost" size="sm" onClick={onBack} className="self-start">
        <ArrowLeft size={15} aria-hidden />
        {t('supply.backToList')}
      </Button>

      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-3">
          {/* Hujjat raqami — monospace: uni og'zaki aytishadi va
              qog'ozdagi bilan solishtirishadi. */}
          <h1 className="font-mono text-xl font-semibold tracking-tight text-text">
            {doc.docNo}
          </h1>
          <Badge tone={tone(doc.status)}>{supplyStatusText(doc.status)}</Badge>
        </div>

        <p className="text-sm text-text-muted">
          <span className="text-text-faint">{t('supply.warehouse')}: </span>
          {source ?? '—'}
        </p>
      </div>

      <dl className="flex flex-wrap gap-x-10 gap-y-4 rounded-[var(--radius-md)] border border-border bg-surface px-5 py-3.5">
        <MetaItem label={t('supply.colPeriod')}>{period ?? '—'}</MetaItem>
        <MetaItem label={t('supply.suppliers')}>{doc.supplierCount}</MetaItem>
        {/* Maxraj — ISH BOR satrlar (`workLineCount`), hujjatdagi hamma
            satr emas. Qoldig'i nol satr joylashtirilmaydi va u maxrajda
            qolsa, hujjat tugaganda ham son to'lmasdi. */}
        <MetaItem label={t('supply.metaPlaced')}>
          {doc.placedLineCount} / {doc.workLineCount}
        </MetaItem>
        <MetaItem label={t('supply.colDate')}>{doc.createdAt.slice(0, 10)}</MetaItem>
      </dl>
    </header>
  );
};
